/**
 * FullProductDetail Component
 *
 * Displays combined catalog + pricing + A+ content data for one ASIN.
 * Structured sections: Header, Quick Facts, Content, Media, Data.
 */

import { useTranslation } from 'react-i18next'
import {
  Image, Tag, BarChart3, ChevronDown, ChevronUp, Package,
  DollarSign, Star, FileText, List, ShoppingCart,
  Truck, Hash, Globe, Box
} from 'lucide-react'
import type { FullProductItem } from '@/lib/api/amazon-sp-api'
import { Card, CardContent } from '@/components/v2/ui/Card'
import { Button } from '@/components/v2/ui/Button'

type AttrEntry = { value?: string | number | boolean; language_tag?: string; marketplace_id?: string; [key: string]: unknown }

interface Props {
  item: FullProductItem
  showRaw: boolean
  onToggleRaw: () => void
}

export function FullProductDetail({ item, showRaw, onToggleRaw }: Props) {
  const attrs = (item.attributes || {}) as Record<string, AttrEntry[]>

  return (
    <div className="space-y-4">
      <ProductHeader item={item} />
      <QuickFacts item={item} />
      {attrs.bullet_point && <BulletPoints bullets={attrs.bullet_point} />}
      {attrs.product_description && <Description entries={attrs.product_description} />}
      <ProductDetails attrs={attrs} item={item} />
      {item.images.length > 0 && <ProductImages images={item.images} />}
      {attrs.ingredients && <TextSection icon={<Package className="h-4 w-4" />} title="Zutaten / Inhaltsstoffe" entries={attrs.ingredients} small />}
      {item.identifiers.length > 0 && <Identifiers identifiers={item.identifiers} />}
      {item.sales_ranks.length > 0 && <SalesRanks salesRanks={item.sales_ranks} />}
      {item.classifications.length > 0 && <Classifications classifications={item.classifications} />}
      <RawJsonToggle showRaw={showRaw} onToggleRaw={onToggleRaw} rawResponse={item.raw_response} />
    </div>
  )
}

function attr(entries: AttrEntry[] | undefined): string | null {
  if (!entries?.[0]) return null
  return String(entries[0].value ?? '')
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ProductHeader({ item }: { item: FullProductItem }) {
  const attrs = (item.attributes || {}) as Record<string, AttrEntry[]>
  const launchDate = attr(attrs.product_site_launch_date)

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex gap-5">
          {item.images[0] && (
            <img src={item.images[0].url} alt={item.title || ''} className="h-28 w-28 object-contain rounded-lg border shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-lg text-neutral-900 dark:text-neutral-50 line-clamp-2 leading-tight">
              {item.title || item.asin}
            </h4>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-neutral-500">
              <span className="font-mono">{item.asin}</span>
              {item.brand && <span><strong>Brand:</strong> {item.brand}</span>}
              {item.manufacturer && item.manufacturer !== item.brand && <span><strong>Hersteller:</strong> {item.manufacturer}</span>}
              {item.product_types.length > 0 && <span><strong>Typ:</strong> {item.product_types.join(', ')}</span>}
              {launchDate && <span><strong>Launch:</strong> {new Date(launchDate).toLocaleDateString('de-DE')}</span>}
            </div>
            {item.is_own_listing !== undefined && (
              <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${item.is_own_listing ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800'}`}>
                {item.is_own_listing ? 'Eigenes Listing' : 'Fremdes Listing'}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Quick Facts
// ---------------------------------------------------------------------------

function QuickFacts({ item }: { item: FullProductItem }) {
  const pricing = item.pricing
  const aplus = item.aplus
  const isOwn = item.is_own_listing

  const priceText = pricing?.buy_box_price != null
    ? `${Number(pricing.buy_box_price).toFixed(2)} ${pricing.buy_box_currency || 'EUR'}`
    : '—'

  const primeText = pricing?.is_prime === true ? 'Ja' : pricing?.is_prime === false ? 'Nein' : '—'

  // A+ Content: for foreign listings show N/A (we can only query our own)
  let aplusText = '—'
  let aplusColor = 'text-neutral-400'
  if (aplus?.has_aplus === true) {
    aplusText = 'Ja'
    aplusColor = 'text-primary-600'
  } else if (isOwn === true) {
    aplusText = 'Nein'
    aplusColor = 'text-neutral-500'
  } else if (isOwn === false) {
    aplusText = 'N/A'
    aplusColor = 'text-red-500'
  }

  const fulfillment = pricing?.fulfillment_channel === 'AMAZON' ? 'FBA' : pricing?.fulfillment_channel === 'MERCHANT' ? 'FBM' : null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <FactCard icon={<DollarSign className="h-4 w-4" />} label="Buy Box" value={priceText} color={pricing?.buy_box_price != null ? 'text-green-600' : 'text-neutral-400'} />
      <FactCard icon={<ShoppingCart className="h-4 w-4" />} label="Prime" value={primeText} color={pricing?.is_prime ? 'text-primary-600' : 'text-neutral-400'} />
      <FactCard icon={<Star className="h-4 w-4" />} label="A+ Content" value={aplusText} color={aplusColor} />
      <FactCard icon={<Tag className="h-4 w-4" />} label="Angebote" value={pricing?.offer_count != null ? String(pricing.offer_count) : '—'} color="text-neutral-600" />
      <FactCard icon={<Truck className="h-4 w-4" />} label="Versand" value={fulfillment || '—'} color={fulfillment === 'FBA' ? 'text-orange-600' : 'text-neutral-400'} />
    </div>
  )
}

function FactCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <Card>
      <CardContent className="p-3 text-center">
        <div className="flex items-center justify-center gap-1 text-neutral-400 mb-1">{icon}<span className="text-xs">{label}</span></div>
        <p className={`text-sm font-semibold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Product Details (attributes grid)
// ---------------------------------------------------------------------------

function ProductDetails({ attrs, item: _item }: { attrs: Record<string, AttrEntry[]>; item: FullProductItem }) {
  const rows: [string, string | null][] = [
    ['Marke', attr(attrs.brand)],
    ['Hersteller', attr(attrs.manufacturer)],
    ['Geschmack / Variante', attr(attrs.flavor)],
    ['Sorte', attr(attrs.variety)],
    ['Größe', attr(attrs.size)],
    ['Stückzahl', attr(attrs.number_of_items)],
    ['Nettogewicht', attrs.net_content_weight ? `${attr(attrs.net_content_weight)} ${attrs.net_content_weight[0]?.unit || 'g'}` : null],
    ['Einzelgewicht', attrs.item_weight ? `${attr(attrs.item_weight)} ${attrs.item_weight[0]?.unit || 'g'}` : null],
    ['Listenpreis (UVP)', attrs.list_price ? `${(attrs.list_price[0] as Record<string, unknown>)?.value_with_tax || '—'} EUR` : null],
    ['Altersgruppe', attr(attrs.age_range_description)],
    ['Küche', attr(attrs.cuisine)],
    ['Haltbarkeit', attrs.fc_shelf_life ? `${attr(attrs.fc_shelf_life)} Tage` : null],
    ['EAN', attrs.externally_assigned_product_identifier ? attr(attrs.externally_assigned_product_identifier) : null],
    ['Lebensmittel', attrs.contains_food_or_beverage?.[0]?.value === true ? 'Ja' : attrs.contains_food_or_beverage?.[0]?.value === false ? 'Nein' : null],
    ['Hitzesensibel', attrs.is_heat_sensitive?.[0]?.value === true ? 'Ja' : attrs.is_heat_sensitive?.[0]?.value === false ? 'Nein' : null],
  ]

  const validRows = rows.filter(([, v]) => v != null && v !== '' && v !== 'null' && v !== 'undefined')
  if (validRows.length === 0) return null

  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Box className="h-4 w-4" /> Produktdetails
        </h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {validRows.map(([label, value]) => (
            <div key={label} className="flex justify-between text-sm py-0.5 border-b border-neutral-100 dark:border-neutral-800">
              <span className="text-neutral-500">{label}</span>
              <span className="font-medium text-neutral-800 dark:text-neutral-200">{value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Bullet Points
// ---------------------------------------------------------------------------

function BulletPoints({ bullets }: { bullets: AttrEntry[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <List className="h-4 w-4" /> Bullet Points ({bullets.length})
        </h5>
        <ul className="space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="text-sm text-neutral-700 dark:text-neutral-300 flex gap-2">
              <span className="text-amber-500 font-bold shrink-0 w-5 text-right">{i + 1}.</span>
              <span className="leading-relaxed">{String(b.value || '')}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Text Sections (Description, Ingredients)
// ---------------------------------------------------------------------------

function Description({ entries }: { entries: AttrEntry[] }) {
  const text = attr(entries)
  if (!text) return null
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Produktbeschreibung
        </h5>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{text}</p>
      </CardContent>
    </Card>
  )
}

function TextSection({ icon, title, entries, small }: { icon: React.ReactNode; title: string; entries: AttrEntry[]; small?: boolean }) {
  const text = attr(entries)
  if (!text) return null
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-2 flex items-center gap-2">{icon} {title}</h5>
        <p className={`${small ? 'text-xs' : 'text-sm'} text-neutral-600 dark:text-neutral-400 leading-relaxed`}>{text}</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

function ProductImages({ images }: { images: FullProductItem['images'] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Image className="h-4 w-4" /> Bilder ({images.length})
        </h5>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <a key={i} href={img.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-center group">
              <img src={img.url} alt={img.variant} className="h-24 w-24 object-contain rounded-lg border group-hover:border-amber-400 transition-colors" />
              <span className="text-xs text-neutral-400 mt-1 block">{img.variant}</span>
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Identifiers, Sales Ranks, Classifications
// ---------------------------------------------------------------------------

function Identifiers({ identifiers }: { identifiers: FullProductItem['identifiers'] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Hash className="h-4 w-4" /> Kennzeichnungen
        </h5>
        <div className="flex flex-wrap gap-2">
          {identifiers.map((id, i) => (
            <span key={i} className="px-2.5 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800 text-xs font-mono">
              <strong className="text-neutral-500">{id.type}:</strong> {id.value}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function SalesRanks({ salesRanks }: { salesRanks: Record<string, unknown>[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Verkaufsränge (BSR)
        </h5>
        <div className="space-y-2">
          {salesRanks.map((rank, i) => {
            const cr = (rank as Record<string, unknown[]>).classificationRanks as Record<string, unknown>[] | undefined
            const dr = (rank as Record<string, unknown[]>).displayGroupRanks as Record<string, unknown>[] | undefined
            return (
              <div key={i} className="space-y-1">
                {cr?.map((c, j) => (
                  <div key={`c${j}`} className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-amber-600 dark:text-amber-400">#{String((c as Record<string, unknown>).rank)}</span>
                    <span className="text-neutral-600 dark:text-neutral-400">{String((c as Record<string, unknown>).title)}</span>
                  </div>
                ))}
                {dr?.map((d, j) => (
                  <div key={`d${j}`} className="flex items-center gap-2 text-xs text-neutral-500">
                    <span className="font-bold">#{String((d as Record<string, unknown>).rank)}</span>
                    <span>{String((d as Record<string, unknown>).title)}</span>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function Classifications({ classifications }: { classifications: Record<string, unknown>[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Globe className="h-4 w-4" /> Kategorien
        </h5>
        {classifications.map((cls, i) => {
          const path = buildCategoryPath((cls as Record<string, unknown[]>).classifications as Record<string, unknown>[] | undefined)
          return (
            <div key={i} className="text-sm text-neutral-600 dark:text-neutral-400">
              {path.map((part, j) => (
                <span key={j}>
                  {j > 0 && <span className="mx-1 text-neutral-300 dark:text-neutral-600">&rsaquo;</span>}
                  <span className={j === path.length - 1 ? 'font-semibold text-neutral-800 dark:text-neutral-200' : ''}>{part}</span>
                </span>
              ))}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

function buildCategoryPath(classifications: Record<string, unknown>[] | undefined): string[] {
  if (!classifications?.length) return []
  const parts: string[] = []
  let node: Record<string, unknown> | undefined = classifications[0]
  while (node) {
    parts.unshift(String(node.displayName || ''))
    node = node.parent as Record<string, unknown> | undefined
  }
  return parts
}

// ---------------------------------------------------------------------------
// Raw JSON
// ---------------------------------------------------------------------------

function RawJsonToggle({ showRaw, onToggleRaw, rawResponse }: { showRaw: boolean; onToggleRaw: () => void; rawResponse: Record<string, unknown> | null }) {
  const { t } = useTranslation()
  return (
    <>
      <Button variant="ghost" size="sm" onClick={onToggleRaw} className="text-neutral-500">
        {showRaw ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
        {t('amazonSpApi.lookup.rawJson')}
      </Button>
      {showRaw && rawResponse && (
        <Card>
          <CardContent className="p-4">
            <pre className="text-xs overflow-auto max-h-96 bg-neutral-50 dark:bg-neutral-900 p-3 rounded font-mono">
              {JSON.stringify(rawResponse, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </>
  )
}
